import { cloneDeep } from 'lodash';
import { trackConfig } from '@/framework/services/history/track-func';
import type { IFramework } from '@/framework/view/framework';
import {
    LayerType,
    MaterialIdsType,
    type IFontBackground,
    type IFontGlow,
    type IFontShadow,
    type IFontStroke,
    type IFontTextGradient,
    type ILayerProps,
    type ITextLayerProps,
    type TextEffectKind,
} from '@/version-types';
import { EffectCreator, FeatureChecker, pushHistory, updateLayer, validateInputLayer } from '../utils';
import { EffectManager } from './effect-manager';
import { type IShapeText } from './shape-text';

export type TextEffectPreset = {
    id: string;
    type: string;
    color: string;
    textShadow: IFontShadow[];
    textGlow: IFontGlow[];
    textBackground: IFontBackground[];
    textStroke: IFontStroke[];
    textGradient?: IFontTextGradient;
    materialID?: string;
};

export type StrokeAdjustPayload = {
    idx: number;
    data: Partial<IFontStroke>;
};

export type ShadowAdjustPayload = {
    idx: number;
    data: Partial<IFontShadow>;
};

export type GlowAdjustPayload = {
    idx: number;
    data: Partial<IFontGlow>;
};

export type BackgroundAdjustPayload = {
    idx: number;
    data: Partial<IFontBackground>;
};

export interface ITextEffect {
    addStroke(layerID: string, options?: { color?: string }): Promise<ILayerProps | void>;
    addShadow(layerID: string, options?: { color?: string }): Promise<ILayerProps | void>;
    addGlow(layerID: string, options?: { color?: string }): Promise<ILayerProps | void>;
    addBackground(layerID: string, options?: { color?: string }): Promise<ILayerProps | void>;
    setEffectVisibility(
        layerID: string,
        effect: TextEffectKind,
        flag: boolean,
        index?: number,
    ): Promise<ILayerProps | void>;
    removeEffect(layerID: string, effect: TextEffectKind, index?: number): Promise<ILayerProps | void>;
    clearAllEffects(layerID: string): Promise<ILayerProps | void>;
    applyEffectPreset(layerID: string, preset: TextEffectPreset): Promise<ILayerProps | void>;
    adjustStrokeProps(
        layerID: string,
        payload: StrokeAdjustPayload,
        options?: { writeHistory?: boolean },
    ): Promise<ILayerProps | void>;
    adjustShadowProps(
        layerID: string,
        payload: ShadowAdjustPayload,
        options?: { writeHistory?: boolean },
    ): Promise<ILayerProps | void>;
    adjustGlowProps(
        layerID: string,
        payload: GlowAdjustPayload,
        options?: { writeHistory?: boolean },
    ): Promise<ILayerProps | void>;
    adjustBackgroundProps(
        layerID: string,
        payload: BackgroundAdjustPayload,
        options?: { writeHistory?: boolean },
    ): Promise<ILayerProps | void>;
}

export class TextEffect implements ITextEffect {
    private effectStroke: EffectManager<'textStroke'>;
    private effectShadow: EffectManager<'textShadow'>;
    private effectGlow: EffectManager<'textGlow'>;
    private effectBackground: EffectManager<'textBackground'>;

    constructor(
        private framework: IFramework,
        private shapeText: IShapeText,
    ) {
        this.effectStroke = new EffectManager(
            framework,
            {
                key: 'textStroke',
                funcID: trackConfig[LayerType.TEXT_LAYER].textStroke,
                maxCount: 3,
                create: (data, color) =>
                    EffectCreator.createStroke(data.fontSize, color || EffectCreator.getRandomColor()),
                refreshShapeText: true,
            },
            shapeText,
        );
        this.effectShadow = new EffectManager(
            framework,
            {
                key: 'textShadow',
                funcID: trackConfig[LayerType.TEXT_LAYER].textShadow,
                maxCount: 1,
                create: (data, color) => EffectCreator.createShadow(data.fontSize, color),
                refreshShapeText: true,
            },
            shapeText,
        );
        this.effectGlow = new EffectManager(
            framework,
            {
                key: 'textGlow',
                funcID: trackConfig[LayerType.TEXT_LAYER].textGlow,
                maxCount: 1,
                create: (data, color) => EffectCreator.createGlow(data.fontSize, color),
                refreshShapeText: true,
            },
            shapeText,
        );
        this.effectBackground = new EffectManager(framework, {
            key: 'textBackground',
            funcID: trackConfig[LayerType.TEXT_LAYER].textBackground,
            maxCount: 1,
            create: (data, color) => EffectCreator.createBackground(data.fontSize, color),
            afterAdd: (layer) => {
                const data = { ...(layer.data as ITextLayerProps) };
                const materialIds = cloneDeep(layer.materialIds || {});

                if (data.textMark || data.textContents?.some((item) => item.textMark)) {
                    data.textMark = undefined;
                    data.textContents = data.textContents?.map((item) => ({ ...item, textMark: undefined }));
                    Reflect.deleteProperty(materialIds, MaterialIdsType.TEXT_MARK);
                }

                return { ...layer, data, materialIds };
            },
        });
    }

    addStroke(layerID: string, options?: { color?: string }) {
        return this.effectStroke.add(layerID, options?.color || EffectCreator.getRandomColor());
    }

    addShadow(layerID: string, options?: { color?: string }) {
        return this.effectShadow.add(layerID, options?.color);
    }

    addGlow(layerID: string, options?: { color?: string }) {
        return this.effectGlow.add(layerID, options?.color);
    }

    addBackground(layerID: string, options?: { color?: string }) {
        return this.effectBackground.add(layerID, options?.color);
    }

    setEffectVisibility(layerID: string, effect: TextEffectKind, flag: boolean, index?: number) {
        switch (effect) {
            case 'textStroke':
                return this.effectStroke.setVisibility(layerID, flag, index);
            case 'textShadow':
                return this.effectShadow.setVisibility(layerID, flag, index);
            case 'textGlow':
                return this.effectGlow.setVisibility(layerID, flag, index);
            case 'textBackground':
                return this.effectBackground.setVisibility(layerID, flag, index);
        }
    }

    removeEffect(layerID: string, effect: TextEffectKind, index?: number) {
        switch (effect) {
            case 'textStroke':
                return this.effectStroke.remove(layerID, index);
            case 'textShadow':
                return this.effectShadow.remove(layerID, index);
            case 'textGlow':
                return this.effectGlow.remove(layerID, index);
            case 'textBackground':
                return this.effectBackground.remove(layerID, index);
        }
    }

    private async refreshShapeTextAsNeeded(layer: ILayerProps) {
        if (!(layer.data as ITextLayerProps).shapeExtension) return layer;
        return (await this.shapeText.refreshShapeTextRender(layer)) || layer;
    }

    async clearAllEffects(layerID: string) {
        const prevLayer = this.framework.service.flatLayer.getLayer(layerID, true);
        if (!validateInputLayer(prevLayer)) return;

        const prepare = (layer: ILayerProps) => {
            const prevLayerData = layer.data as ITextLayerProps;
            const color = '#000000ff';
            const textContents = (prevLayerData.textContents || []).map((item) => ({ ...item, color }));
            const data: ITextLayerProps = {
                ...prevLayerData,
                textContents,
                color,
                textGradient: null,
                textStroke: [],
                textShadow: [],
                textGlow: [],
                textBackground: [],
            };
            const materialIds = cloneDeep(layer.materialIds || {});
            Reflect.deleteProperty(materialIds, MaterialIdsType.TEXT_EFFECT);

            const nextLayer: ILayerProps = { ...layer, data, materialIds };
            return nextLayer;
        };

        if (prevLayer.layerType === LayerType.GROUP_LAYER) {
            const children = prevLayer.children.map(prepare);
            const nextLayer: ILayerProps = { ...prevLayer, children };

            updateLayer(this.framework, nextLayer);
            pushHistory(this.framework, prevLayer, nextLayer);
            return nextLayer;
        }

        const hasNoEffects = FeatureChecker.hasNoEffects(prevLayer);
        const isUsingGradient = FeatureChecker.usingGradient(prevLayer);
        if (hasNoEffects && !isUsingGradient) return prevLayer;

        let nextLayer: ILayerProps = prepare(prevLayer);
        nextLayer = await this.refreshShapeTextAsNeeded(nextLayer);

        updateLayer(this.framework, nextLayer);
        pushHistory(this.framework, prevLayer, nextLayer);
        return nextLayer;
    }

    async applyEffectPreset(layerID: string, preset: TextEffectPreset) {
        const prevLayer = this.framework.service.flatLayer.getLayer(layerID, true);
        if (!validateInputLayer(prevLayer)) return;

        const prepare = (layer: ILayerProps, p: TextEffectPreset) => {
            const layerData = layer.data as ITextLayerProps;
            const { materialID, color, textStroke, textGlow, textShadow, textBackground, textGradient = null } = p;
            const textContents = (layerData.textContents || []).map((item) => ({
                ...item,
                color,
                textMark: textBackground.length > 0 ? undefined : item.textMark,
                underline: textGradient ? false : item.underline,
                lineThrough: textGradient ? false : item.lineThrough,
            }));
            const data: ITextLayerProps = {
                ...layerData,
                textContents,
                color,
                textStroke,
                textGlow,
                textShadow,
                textBackground,
                textGradient,
                textMark: textBackground.length > 0 ? null : layerData.textMark,
                lineThrough: textGradient ? false : layerData.lineThrough,
                underline: textGradient ? false : layerData.underline,
                texture: undefined,
            };

            const materialIds = cloneDeep(layer.materialIds || {});
            if (materialID) {
                materialIds[MaterialIdsType.TEXT_EFFECT] = [String(materialID)];
            } else {
                Reflect.deleteProperty(materialIds, MaterialIdsType.TEXT_EFFECT);
            }
            if (textBackground.length > 0) Reflect.deleteProperty(materialIds, MaterialIdsType.TEXT_MARK);

            const nextLayer: ILayerProps = { ...layer, data, materialIds };
            return nextLayer;
        };

        if (prevLayer.layerType === LayerType.GROUP_LAYER) {
            const children = prevLayer.children.map((child) => prepare(child, preset));
            const nextLayer: ILayerProps = { ...prevLayer, children };

            updateLayer(this.framework, nextLayer);
            pushHistory(this.framework, prevLayer, nextLayer, trackConfig[LayerType.TEXT_LAYER].effects);
            return nextLayer;
        }

        let nextLayer: ILayerProps = prepare(prevLayer, preset);
        nextLayer = await this.refreshShapeTextAsNeeded(nextLayer);

        updateLayer(this.framework, nextLayer);
        pushHistory(this.framework, prevLayer, nextLayer, trackConfig[LayerType.TEXT_LAYER].effects);
        return nextLayer;
    }

    adjustStrokeProps(layerID: string, payload: StrokeAdjustPayload, options?: { writeHistory?: boolean }) {
        return this.effectStroke.adjust(layerID, payload, options);
    }

    adjustShadowProps(layerID: string, payload: ShadowAdjustPayload, options?: { writeHistory?: boolean }) {
        return this.effectShadow.adjust(layerID, payload, options);
    }

    adjustGlowProps(layerID: string, payload: GlowAdjustPayload, options?: { writeHistory?: boolean }) {
        return this.effectGlow.adjust(layerID, payload, options);
    }

    adjustBackgroundProps(layerID: string, payload: BackgroundAdjustPayload, options?: { writeHistory?: boolean }) {
        return this.effectBackground.adjust(layerID, payload, options);
    }
}
